'use strict';

DataQueryColumnFactory.$inject = ['$q', 'mx.shell.Config', 'mx.SolutionBuilderAgent.EntityService', 'mx.sb.Agent.DataQuery.Enums', 'mx.sb.Agent.DataQuery.ColumnFiltersFactory'];
export default function DataQueryColumnFactory($q, configService, entityService, DataQueryEnums, columnFiltersFactory) {
	const simpleExpression = /^\[(\w*)]$|^(\w*)$/;
	const regAsql = /^(?:(T)\((\w+)\)|(\w+))(?:\.\w+)+\w+$|^(S)ubquery\(/gi;
	const regSubqueryParams = /base\.(\w+)/gi;

	/**
	 * @ngdoc Class
	 * @name mx.sb.Agent.DataQuery.Column
	 *
	 * @description
	 * Class DataQueryColumn
	 *
	 * @param {Object} columnDefinition - attributes of column
	 *
	 */
	function DataQueryColumn(columnDefinition) {
		const { ColumnAlignmentsEnum, ColumnSortingEnum, ColumnTitleTypeEnum, ColumnDisplayTypeEnum, ColumnSearchTypeEnum } = DataQueryEnums;

		let position,
			sorting,
			sortingPriority,
			title,
			titleType,
			width,
			displayType,
			isDisplay,
			isLoading,
			attributeValueType,
			groupingIsAvailable,
			isEnum,
			isPickup,
			visible,
			expressionResult,
			enumeration,
			enumerationAsObject,
			isSingleColumn;

		attributeValueType = columnDefinition.AttributeValueType || mx.shell.ObjectPropertyType.String.name;

		titleType = ColumnTitleTypeEnum.containsValue(columnDefinition.TitleType) ? columnDefinition.TitleType : ColumnAlignmentsEnum.IndividualDisplayString;

		title = titleType === ColumnTitleTypeEnum.NoTitle ? '' : columnDefinition.Title || '';

		displayType = ColumnDisplayTypeEnum.containsValue(columnDefinition.DisplayType) ? columnDefinition.DisplayType : ColumnDisplayTypeEnum.Visible;

		isDisplay = displayType !== ColumnDisplayTypeEnum.Hidden && displayType !== ColumnDisplayTypeEnum.None;
		isLoading = displayType !== ColumnDisplayTypeEnum.Hidden;
		visible = isDisplay && displayType !== ColumnDisplayTypeEnum.InvisibleByDefault;

		groupingIsAvailable = attributeValueType !== mx.shell.ObjectPropertyType.Date.name && attributeValueType !== mx.shell.ObjectPropertyType.Boolean.name;

		isEnum = (Boolean(columnDefinition.ReferenceTo) && attributeValueType === mx.shell.ObjectPropertyType.Integer.name) || Array.isArray(columnDefinition.Enumeration);
		isPickup = Boolean(columnDefinition.ReferenceTo) && attributeValueType === mx.shell.ObjectPropertyType.Guid.name;

		columnDefinition.Expression = typeof columnDefinition.Expression === 'string' ? columnDefinition.Expression : '';
		isSingleColumn = !isEnum && !isPickup && !columnDefinition.Expression.match(regAsql);

		expressionResult = this.checkExpression(columnDefinition.Expression);

		Object.defineProperties(this, {
			Alignment: {
				enumerable: true,
				value: ColumnAlignmentsEnum.containsValue(columnDefinition.Alignment) ? columnDefinition.Alignment : ColumnAlignmentsEnum.Left,
			},
			AttributeSearchValueType: {
				enumerable: true,
				value: attributeValueType,
			},
			AttributeValueType: {
				enumerable: true,
				value: columnDefinition.AttributeValueType || mx.shell.ObjectPropertyType.String.name,
			},
			ValueType: {
				enumerable: true,
				value: columnDefinition.ValueType,
			},
			Type: {
				enumerable: true,
				value: columnDefinition.Type,
			},
			Expression: {
				enumerable: true,
				value: columnDefinition.Expression || '',
			},
			HasQuery: {
				enumerable: true,
				value: expressionResult.hasQuery,
			},
			ExpressionDependencies: {
				enumerable: true,
				value: expressionResult.expressionDependencies,
			},
			ExpressionType: {
				enumerable: true,
				value: expressionResult.expressionType,
			},
			ExpressionClassName: {
				enumerable: true,
				value: expressionResult.expressionClassName,
			},
			RawValuePropertyName: {
				enumerable: true,
				value: expressionResult.rawValuePropertyName,
			},
			TargetAttributePaths: {
				enumerable: true,
				value: Array.isArray(columnDefinition.TargetAttributePaths) ? columnDefinition.TargetAttributePaths : [],
			},
			SearchExpression: {
				enumerable: true,
				value: columnDefinition.SearchExpression || '',
			},
			SortingExpression: {
				enumerable: true,
				value: columnDefinition.SortingExpression || '',
			},
			Format: {
				enumerable: true,
				writable: true,
				value: columnDefinition.Format || (attributeValueType === mx.shell.ObjectPropertyType.Date.name ? 'short' : ''),
			},
			Id: {
				enumerable: true,
				value: columnDefinition.Id || '',
			},
			IsSystem: {
				enumerable: true,
				value: Boolean(columnDefinition.IsSystem),
			},
			Required: {
				enumerable: true,
				value: Boolean(columnDefinition.Required),
			},
			Name: {
				enumerable: true,
				writable: true,
				value: columnDefinition.Name || '',
			},
			ReferenceTo: {
				enumerable: true,
				value: columnDefinition.ReferenceTo,
			},
			ReferenceFilter: {
				enumerable: true,
				value: columnDefinition.ReferenceFilter,
			},
			Enumeration: {
				enumerable: true,
				get: function () {
					return enumeration;
				},
				set: function (value) {
					if (Array.isArray(value)) {
						enumeration = value;
						enumerationAsObject = {};
						enumeration.forEach(function (item) {
							enumerationAsObject[item.value] = item;
						});
					}
				},
			},
			EnumerationAsObject: {
				enumerable: true,
				get: function () {
					return enumerationAsObject;
				},
			},
			ColorScript: {
				enumerable: true,
				value: columnDefinition.ColorScript,
			},
			ColorType: {
				enumerable: true,
				value: columnDefinition.ColorType,
			},
			Color: {
				enumerable: true,
				value: columnDefinition.Color,
			},
			DisplayImage: {
				enumerable: true,
				value: columnDefinition.DisplayImage,
			},
			ImageScript: {
				enumerable: true,
				value: columnDefinition.ImageScript,
			},
			IsAvailableInSearch: {
				enumerable: true,
				value: isDisplay && columnDefinition.SearchDisplayType !== ColumnSearchTypeEnum.No,
			},
			ReadOnly: {
				enumerable: true,
				value: columnDefinition.ReadOnly,
			},
			IsArray: {
				enumerable: true,
				value: columnDefinition.IsArray,
			},
			Title: {
				enumerable: true,
				value: title,
			},
			Scale: {
				enumerable: true,
				value: columnDefinition.Scale,
			},
			TitleType: {
				enumerable: true,
				value: titleType,
			},
			DisplayType: {
				enumerable: true,
				value: displayType,
			},
			IsDisplay: {
				enumerable: true,
				value: isDisplay,
			},
			IsLoading: {
				enumerable: true,
				value: isLoading,
			},
			IsEnum: {
				enumerable: true,
				value: isEnum,
			},
			IsPickup: {
				enumerable: true,
				value: isPickup,
			},
			IsSingleColumn: {
				enumerable: true,
				value: isSingleColumn,
			},
			IsKeyword: {
				enumerable: true,
				writable: true,
				value: Boolean(columnDefinition.IsKeyword),
			},
			GroupingIsAvailable: {
				enumerable: true,
				writable: true,
				value: groupingIsAvailable,
			},

			// Configurable attributes
			Width: {
				enumerable: true,
				get: function () {
					return width;
				},
				set: function (value) {
					value = parseInt(value);
					width = !isNaN(value) && value >= 0 ? value : 0;
				},
			},
			Position: {
				enumerable: true,
				get: function () {
					return position;
				},
				set: function (value) {
					value = parseInt(value);
					position = !isNaN(value) && value >= 0 ? value : 0;
				},
			},
			Sorting: {
				enumerable: true,
				get: function () {
					return sorting;
				},
				set: function (value) {
					if (ColumnSortingEnum.containsValue(value)) {
						sorting = value;
					} else {
						sorting = ColumnSortingEnum.None;
					}
				},
			},
			SortingPriority: {
				enumerable: true,
				get: function () {
					return sortingPriority;
				},
				set: function (value) {
					value = parseInt(value);
					sortingPriority = !isNaN(value) && value >= 0 ? value : 0;
				},
			},
			AriaHidden: {
				enumerable: true,
				value: false,
			},
		});

		this.visible = visible; //TODO: move definition from grid
		this.Width = columnDefinition.Width;
		this.Position = columnDefinition.Position;
		this.Sorting = columnDefinition.Sorting;
		this.SortingPriority = columnDefinition.SortingPriority;
		this.Enumeration = columnDefinition.Enumeration;

		this.filter = columnFiltersFactory.initColumnFilter(this);
	}

	DataQueryColumn.prototype = {
		getEnumValues: function () {
			if (!this.IsEnum) {
				return $q.when([]);
			}
			if (this.Enumeration) {
				return $q.when(this.Enumeration);
			}
			this._enumItemsLoader =
				this._enumItemsLoader ||
				entityService.getEnumerationValues(this.ReferenceTo, this.ReferenceFilter).then(
					function (enumeration) {
						this.Enumeration = enumeration.map(function (item) {
							return {
								title: item.DisplayString,
								hidden: item.Hidden,
								value: item.Value,
							};
						});

						return this.Enumeration;
					}.bind(this)
				);

			return this._enumItemsLoader;
		},
		getEnumValueTitle: function (value) {
			return this.getEnumValues().then(
				function () {
					return (this.EnumerationAsObject[value] || {}).title;
				}.bind(this)
			);
		},

		checkExpression: function (expression) {
			var expressionResult = {
				hasQuery: false,
			};

			var match = simpleExpression.exec(expression);
			if (match) {
				expressionResult.rawValuePropertyName = match[1] || match[2];
				return expressionResult;
			} else {
				expressionResult.hasQuery = true;
			}

			expressionResult.expressionDependencies = [];

			var result = regAsql.exec(expression);
			if (result) {
				expressionResult.hasQuery = true;

				var expType = result[1] || result[4];
				expressionResult.expressionType = expType ? expType.toLocaleUpperCase() : 'D';
				regAsql.lastIndex = 0;

				// 'T': T(PDRActionClassBase).Name
				// 'D': Action.Name
				// 'S': Subquery(...)
				switch (expressionResult.expressionType) {
					case 'T':
						expressionResult.expressionClassName = result[2];
						expressionResult.expressionDependencies.push(expression.split('.')[1]);
						break;
					case 'D':
						expressionResult.expressionDependencies.push(result[3]);
						expressionResult.rawValuePropertyName = result[3];
						break;
					default:
						var param;
						while ((param = regSubqueryParams.exec(expression)) !== null) {
							expressionResult.expressionDependencies.push(param[1]);
						}
				}
			}

			return expressionResult;
		},
	};

	return DataQueryColumn;
}
